import { useCallback, useState } from 'react'

import { productIdentityKeys } from '@repo/types'

import { useImportDemoProduct } from 'hooks/api'

import { productLinkFor } from './productLink'
import type { ProductFormRow } from './types'

interface UseProductImportParams {
  /**
   * RHF's `prepend` for the products field array.
   *
   * PREPEND, not append: the product you just imported is the one you want to
   * look at, and appending buried it under rows you had already reviewed.
   */
  prepend: (row: ProductFormRow) => void
  /**
   * VRG-716 — the rows already listed (the watched values), so a paste of a product
   * that is already there adds nothing. Read at click time, never cached.
   */
  rows: readonly ProductFormRow[] | undefined
  /**
   * VRG-716 — called when Shopify's own product API answered the import: proof the store is on
   * Shopify, which the create flow uses when the homepage could not be read.
   */
  onShopifyEvidence?: () => void
}

/** The identities a listed row answers to — see `productIdentityKeys` in @repo/types. */
const keysOf = (row: Pick<ProductFormRow, 'sourceUrl' | 'productUrl' | 'productUrlKind' | 'sku'>) =>
  productIdentityKeys({
    // A `search` productUrl is the store's search for the title, not the product: never a key.
    urls: [row.sourceUrl, row.productUrlKind === 'page' ? row.productUrl : null],
    sku: row.sku,
  })

/**
 * VRG-308 AC3 — "paste-a-URL / manual-entry fallback, never a dead end".
 *
 * VRG-716 — this USED to call the propose route (`useProposeDemo`), which runs the
 * whole first-step seeder: on a product page with no schema.org Product markup it
 * falls through to the store's `/products.json`, so pasting one product re-added
 * the catalogue's first five (ofyshop.com, 2026-10-16). It now calls
 * `/demos/admin/import-product`, which returns exactly the product the link names
 * or nothing with a reason, and it skips anything already listed (by listing URL,
 * Shopify handle or SKU) with a short note instead of a duplicate.
 *
 * Hoisted out of `CreateDemoModal` the moment a second caller appeared (VRG-369's
 * Add-products modal on the Demos console) rather than after a third, per
 * working-conventions §36.
 */
export const useProductImport = ({ prepend, rows, onShopifyEvidence }: UseProductImportParams) => {
  const [importUrl, setImportUrl] = useState('')
  const [importError, setImportError] = useState<string | null>(null)
  const [importNotice, setImportNotice] = useState<string | null>(null)
  const { mutateAsync: importOne, isPending: isImporting } = useImportDemoProduct()

  const handleImport = useCallback(async () => {
    const url = importUrl.trim()
    if (!url) return
    setImportError(null)
    setImportNotice(null)
    try {
      const data = await importOne(url)
      const found = data?.products ?? []
      if (!found.length) {
        // Reports the importer's own reason rather than a generic failure — it
        // distinguishes "that is a shop link" from "this page has no product",
        // which changes what the AE should do next.
        setImportError(data?.reason ?? 'Could not read a product from that URL. Enter the details by hand below.')
        return
      }

      if (data.path === 'shopify') onShopifyEvidence?.()
      const listed = new Set((rows ?? []).flatMap((row) => keysOf(row)))
      const pastedKeys = productIdentityKeys({ urls: [url] })
      const duplicates: string[] = []
      // `.reverse()` so several products keep the site's own order once each is
      // unshifted onto the front. (The importer returns one today.)
      for (const prod of [...found].reverse()) {
        const sourceUrl = prod.source?.url ?? url
        const link = productLinkFor(sourceUrl, prod.title ?? '', data.path, prod.productUrl)
        const row: ProductFormRow = {
          title: prod.title ?? '',
          sku: prod.sku ?? '',
          description: prod.description ?? '',
          images: prod.images ?? [],
          // Provenance: which URL this came from (VRG-308 AC7).
          sourceUrl,
          ...link,
          // Deliberately pre-ticked: pasting a specific listing IS the decision
          // to include it. Making the AE then hunt for its checkbox would be a
          // second confirmation of something they just asked for by hand.
          selected: true,
          manual: false,
          // VRG-723 — what this import could not pull in, shown on the card.
          missing: prod.missing ?? [],
        }
        const rowKeys = [...keysOf(row), ...pastedKeys]
        if (rowKeys.some((k) => listed.has(k))) {
          duplicates.push(row.title.trim() || 'That product')
          continue
        }
        rowKeys.forEach((k) => listed.add(k))
        prepend(row)
      }
      if (duplicates.length) {
        setImportNotice(`Already in the list: ${duplicates.join(', ')}.`)
      }
      setImportUrl('')
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Could not read that URL.')
    }
  }, [importUrl, prepend, importOne, rows, onShopifyEvidence])

  const reset = useCallback(() => {
    setImportUrl('')
    setImportError(null)
    setImportNotice(null)
  }, [])

  return {
    importUrl,
    onImportUrlChange: (value: string) => {
      setImportUrl(value)
      setImportNotice(null)
    },
    onImport: () => void handleImport(),
    isImporting,
    importError,
    importNotice,
    reset,
  }
}
